import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  isLowPowerDevice,
  isMobileBreakpoint,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/** Émis par drag-zoom-wrapper quand une photo dépasse 1x (même nom dans ce fichier-là). */
const ZOOM_START_EVENT = 'drag-zoom:start';

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} [gallery] - The horizontal scroller holding the media.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.gallery?.addEventListener('scroll', this.handleScroll);
    this.addEventListener(ZOOM_START_EVENT, this.#handleZoomStart);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Retirée du document encore ouverte (rendu de section) : aucun événement close.
    if (this.refs.dialog.open) this.#releasePage();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.gallery?.removeEventListener('scroll', this.handleScroll);
    this.removeEventListener(ZOOM_START_EVENT, this.#handleZoomStart);
  }

  /**
   * Charge et décode d'avance la photo d'index donné et ses deux voisines, à leur
   * taille d'affichage. En loading="lazy", la photo suivante ne se chargeait
   * qu'en arrivant dessus : la transition montrait une moitié d'écran blanche,
   * puis la photo surgissait.
   *
   * @param {number} index
   */
  #warmUpAround(index) {
    const { media } = this.refs;
    if (!media.length) return;

    for (const offset of [0, 1, -1]) {
      const container = media[(index + offset + media.length) % media.length];
      const image = container?.querySelector('img.product-media__image');
      if (!(image instanceof HTMLImageElement)) continue;

      if (image.loading === 'lazy') image.loading = 'eager';
      image.decode().catch(() => {});
    }
  }

  /**
   * Survol d'une photo de la fiche : prépare la visionneuse avant le clic.
   *
   * @param {number} index
   */
  preloadMedia(index) {
    this.#warmUpAround(index);
  }

  /**
   * La haute définition n'est chargée que lorsqu'on zoome une photo.
   *
   * @param {Event} event
   */
  #handleZoomStart = (event) => {
    const container = event.target instanceof Element ? event.target.closest('.product-media-container--image') : null;
    if (container instanceof HTMLElement) this.loadHighResolutionImage(container);
  };

  /** Index of the media closest to the current scroll position. */
  get #currentIndex() {
    const { gallery, media } = this.refs;
    if (!gallery) return 0;

    const index = Math.round(Math.abs(gallery.scrollLeft) / Math.max(1, gallery.clientWidth));
    return Math.min(media.length - 1, Math.max(0, index));
  }

  /**
   * Scrolls the gallery to the media at the given index (wraps around).
   * @param {number} index
   */
  #goTo(index) {
    const { media } = this.refs;
    if (!media.length) return;

    const targetIndex = (index + media.length) % media.length;
    const target = media[targetIndex];
    if (!target) return;

    this.#warmUpAround(targetIndex);
    target.scrollIntoView({
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }

  /** Shows the next media. */
  next() {
    this.#goTo(this.#currentIndex + 1);
  }

  /** Shows the previous media. */
  previous() {
    this.#goTo(this.#currentIndex - 1);
  }

  /**
   * Desktop : un clic sur l'image passe à l'image suivante.
   * Mobile : aucune action au tap (navigation au swipe + flèches uniquement,
   * pour ne pas entrer en conflit avec pinch/double-tap).
   * Une image zoomée se déplace au drag : le clic ne navigue pas.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  handleMediaClick(event) {
    if (isMobileBreakpoint()) return;

    // event.target est proxifié vers l'élément portant on:click (le <li>) :
    // le wrapper de zoom est donc un descendant, pas un ancêtre.
    const container = event.target instanceof Element ? event.target : null;
    const wrapper = /** @type {import('./drag-zoom-wrapper').DragZoomWrapper | null} */ (
      container?.querySelector('drag-zoom-wrapper')
    );
    if (wrapper?.isZoomed) return;

    this.next();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();
    this.#warmUpAround(index);

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      this.#showModalUnzoomed();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    // Mobile : la morphologie View Transition est saccadée (image 3/4 rognée → plein écran).
    if (
      !supportsViewTransitions() ||
      isLowPowerDevice() ||
      isMobileBreakpoint() ||
      !sourceImage ||
      !targetImage
    ) {
      this.#openWithFade(index, open);
      return;
    }

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container.
   * Appelée seulement au zoom : charger l'original (3840, 17 Mpx) à chaque
   * changement de photo faisait décoder l'image en pleine transition.
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    // Marquée dès la demande : un pincement émet des dizaines d'événements.
    this.#highResImagesLoaded.add(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.decoding = 'async';
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');
    newImage.src = highResolutionUrl;

    // Décodée avant l'échange, sinon le navigateur la décode au moment de la
    // peindre et l'image se fige pendant le zoom.
    newImage
      .decode()
      .then(() => {
        if (image.isConnected) image.replaceWith(newImage);
      })
      .catch(() => this.#highResImagesLoaded.delete(highResolutionUrl));
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    // Au glissé (mobile) comme au clic : la photo d'après est prête avant le geste suivant.
    this.#warmUpAround(activeIndex);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice() || isMobileBreakpoint()) {
      return this.#closeWithFade();
    }

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    // Avant close() : la mise en page de la fermeture rend alors à Chrome la
    // balise viewport d'origine, donc le zoom libre de la page.
    this.#releasePage();
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Ouvre la visionneuse avec la page ramenée à l'échelle 1 (voir lockPageScale),
   * ou calée sur la zone visible là où le navigateur garde le zoom.
   */
  #showModalUnzoomed() {
    const { dialog } = this.refs;

    // Avant showModal() : c'est la mise en page de l'ouverture qui applique la
    // balise, et la croix, qui y reçoit le focus, est déjà dans la zone visible :
    // le navigateur n'a pas à y déplacer la vue, qu'on retrouve à la fermeture.
    lockPageScale();
    this.#fitDialog();
    dialog.showModal();
    // Forcer cette mise en page tout de suite : l'échelle relue ensuite est la vraie.
    void dialog.offsetWidth;
    this.#fitDialog();

    window.visualViewport?.addEventListener('resize', this.#fitDialog);
    window.visualViewport?.addEventListener('scroll', this.#fitDialog);
    // Couvre aussi les fermetures qui ne passent pas par closeDialog() (geste
    // retour d'Android, page rendue par le cache de navigation).
    dialog.addEventListener('close', this.#releasePage, { once: true });
  }

  #fitDialog = () => fitToVisualViewport(this.refs.dialog);

  /** Rend à la page son zoom libre et à la visionneuse sa taille normale. */
  #releasePage = () => {
    window.visualViewport?.removeEventListener('resize', this.#fitDialog);
    window.visualViewport?.removeEventListener('scroll', this.#fitDialog);
    clearVisualViewportFit(this.refs.dialog);
    unlockPageScale();
  };

  /**
   * Ouverture mobile / fallback : fondu fluide sans morphologie View Transition.
   * @param {number} index
   * @param {() => void} openFn
   */
  #openWithFade(index, openFn) {
    const { dialog } = this.refs;

    dialog.classList.remove('dialog-zoomed-gallery--animate-out');
    dialog.classList.add('dialog-zoomed-gallery--animate-in');
    openFn();
    this.selectThumbnail(index, { behavior: 'instant' });
    this.#clearFadeClass(dialog, 'dialog-zoomed-gallery--animate-in');
  }

  /**
   * Fermeture mobile / fallback : fondu avant close().
   * @returns {Promise<void>}
   */
  async #closeWithFade() {
    const { dialog } = this.refs;

    if (prefersReducedMotion()) return this.closeDialog();

    dialog.classList.remove('dialog-zoomed-gallery--animate-in');
    dialog.classList.add('dialog-zoomed-gallery--animate-out');

    await new Promise((resolve) => {
      const done = () => resolve();
      dialog.addEventListener('animationend', done, { once: true });
      setTimeout(done, 320);
    });

    dialog.classList.remove('dialog-zoomed-gallery--animate-out');
    this.closeDialog();
  }

  /**
   * @param {HTMLDialogElement} dialog
   * @param {string} className
   */
  #clearFadeClass(dialog, className) {
    if (prefersReducedMotion()) {
      dialog.classList.remove(className);
      return;
    }

    const done = () => dialog.classList.remove(className);
    dialog.addEventListener('animationend', done, { once: true });
    setTimeout(done, 320);
  }

  /**
   * Keyboard support: arrow keys navigate, escape closes.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail whose media to prepare.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.#warmUpAround(index);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      this.#warmUpAround(index);
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        // La galerie qui défile, pas l'écran : quand la visionneuse est mise à
        // l'échelle d'une page zoomée, les photos voisines entrent dans l'écran.
        root: elements[0]?.parentElement ?? null,
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}

/*
 * Zoom de la page pendant la visionneuse.
 *
 * Comme tout élément fixe, la visionneuse grossit avec la page quand celle-ci a
 * été zoomée au pincement : on n'en voyait qu'un morceau, souvent sans la croix,
 * et ses propres gestes (pincement de la photo, glissé d'une photo à l'autre)
 * confisquaient ceux qui auraient permis de dézoomer.
 *
 * La page est donc ramenée à l'échelle 1 le temps de la visionneuse, en bornant
 * la balise viewport à 1 : Chrome, Samsung Internet et les WebView Android
 * l'appliquent à la mise en page de l'ouverture, puis refusent tout nouveau zoom
 * de la page jusqu'à la fermeture. Safari iOS (donc tout navigateur d'iPhone)
 * n'abaisse jamais un zoom de l'utilisateur, balise ou pas : la visionneuse y est
 * calée sur la zone visible, à l'échelle inverse du zoom.
 */

/** Écart à 1 en deçà duquel la page est considérée comme non zoomée. */
const PAGE_SCALE_EPSILON = 0.01;

/** Balise viewport d'origine, rendue à la fermeture. @type {{ meta: HTMLMetaElement, content: string } | null} */
let viewportLock = null;

function lockPageScale() {
  if (viewportLock) return;

  const meta = document.querySelector('meta[name="viewport"]');
  const content = meta?.getAttribute('content');
  if (!(meta instanceof HTMLMetaElement) || content == null) return;

  viewportLock = { meta, content };
  const lockedContent = content
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part && !/^(minimum|maximum)-scale\s*=/i.test(part))
    .concat('minimum-scale=1', 'maximum-scale=1')
    .join(',');
  meta.setAttribute('content', lockedContent);
}

function unlockPageScale() {
  if (!viewportLock) return;

  viewportLock.meta.setAttribute('content', viewportLock.content);
  viewportLock = null;
}

const VISUAL_VIEWPORT_FIT_PROPERTIES = [
  'inset',
  'width',
  'height',
  'max-width',
  'max-height',
  'transform-origin',
  'transform',
];

/**
 * Page restée zoomée : pose le dialog sur la zone visible, à l'échelle inverse du
 * zoom. Il garde la mise en page d'un écran non zoomé, et le transform en fait la
 * référence de ses enfants fixes (croix, flèches, miniatures), qui le suivent.
 *
 * @param {HTMLDialogElement} dialog
 */
function fitToVisualViewport(dialog) {
  const viewport = window.visualViewport;
  if (!viewport || Math.abs(viewport.scale - 1) <= PAGE_SCALE_EPSILON) {
    clearVisualViewportFit(dialog);
    return;
  }

  const { scale, width, height, offsetLeft, offsetTop } = viewport;
  dialog.style.setProperty('inset', '0 auto auto 0');
  dialog.style.setProperty('width', `${width * scale}px`);
  dialog.style.setProperty('height', `${height * scale}px`);
  dialog.style.setProperty('max-width', 'none');
  dialog.style.setProperty('max-height', 'none');
  dialog.style.setProperty('transform-origin', '0 0');
  dialog.style.setProperty('transform', `translate(${offsetLeft}px, ${offsetTop}px) scale(${1 / scale})`);
}

/** @param {HTMLDialogElement} dialog */
function clearVisualViewportFit(dialog) {
  for (const property of VISUAL_VIEWPORT_FIT_PROPERTIES) dialog.style.removeProperty(property);
}
